import { useEffect, useState } from "react";
import DepartmentTypeSettings from "../../components/helpdesk/DepartmentTypeSettings";
import { DepartmentsAPI } from "../../api/admin";

export default function DepartmentList() {
  const [departments, setDepartments] = useState([]);
  const [newName, setNewName] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [selectedDefaults, setSelectedDefaults] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [newMailboxEmail, setNewMailboxEmail] = useState("");
  const [newMailboxDepartment, setNewMailboxDepartment] = useState("");
  const [mailboxSaving, setMailboxSaving] = useState(false);

  useEffect(() => {
    loadDepartments();
    loadRecipes();
    loadMailboxes();
  }, []);

  async function loadDepartments() {
    try {
      const res = await DepartmentsAPI.list();
      setDepartments(res);
      setSelectedDefaults(Object.fromEntries(res.map((d) => [d.id, d.default_recipe_id || ""])));
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadRecipes() {
    try {
      const res = await DepartmentsAPI.listDefaultRecipes();
      setRecipes(res);
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveDefault(id) {
    setSavingId(id);
    setError(null);
    setNotice("");
    try {
      await DepartmentsAPI.update(id, { default_recipe_id: selectedDefaults[id] || null });
      await loadDepartments();
      setNotice("Default recipe saved.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await DepartmentsAPI.create({ name: newName });
      setNewName("");
      await loadDepartments();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this department?")) return;
    try {
      await DepartmentsAPI.delete(id);
      await loadDepartments();
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadMailboxes() {
    try {
      const res = await DepartmentsAPI.listInboundMailboxes();
      setMailboxes(res);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleCreateMailbox(e) {
    e.preventDefault();

    setMailboxSaving(true);
    setError(null);
    setNotice("");

    try {
      await DepartmentsAPI.createInboundMailbox({
        email_address: newMailboxEmail,
        department_id: newMailboxDepartment || null,
      });

      setNewMailboxEmail("");
      setNewMailboxDepartment("");

      await loadMailboxes();

      setNotice("Inbound mailbox added.");
    } catch (err) {
      setError(err.message);
    } finally {
      setMailboxSaving(false);
    }
  }

  async function handleMailboxDepartmentChange(id, departmentId) {
    try {
      const mailbox = mailboxes.find((m) => m.id === id);

      await DepartmentsAPI.updateInboundMailbox(id, {
        department_id: departmentId || null,
        is_active: mailbox.is_active,
      });

      await loadMailboxes();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleMailboxActiveChange(id, isActive) {
    try {
      const mailbox = mailboxes.find((m) => m.id === id);

      await DepartmentsAPI.updateInboundMailbox(id, {
        department_id: mailbox.department_id || null,
        is_active: isActive,
      });

      await loadMailboxes();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeleteMailbox(id) {
    if (!window.confirm("Delete this inbound mailbox?")) return;

    try {
      await DepartmentsAPI.deleteInboundMailbox(id);
      await loadMailboxes();
      setNotice("Inbound mailbox deleted.");
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Manage Departments</h1>
      {error && <p className="text-red-500">{error}</p>}
      {notice && <p role="status" className="text-[var(--nj-text)]">{notice}</p>}

      <form onSubmit={handleCreate} className="flex gap-2 mb-4">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New department name"
          className="border rounded p-2 flex-1"
        />
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          Add
        </button>
      </form>

      <ul className="border rounded divide-y">
        {departments.map((d) => (
          <li key={d.id} className="flex flex-wrap gap-3 items-center p-3">
            <span className="min-w-32 font-medium text-[var(--nj-text)]">{d.name}</span>
            <label className="flex-1 min-w-52 text-sm text-[var(--nj-text)]">
              Default reply recipe
              <select
                className="block w-full mt-1 rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] text-[var(--nj-text)] p-2"
                value={selectedDefaults[d.id] || ""}
                onChange={(e) => setSelectedDefaults((prev) => ({ ...prev, [d.id]: e.target.value }))}
              >
                <option value="">None</option>
                {d.default_recipe_id && !recipes.some((r) => r.id === d.default_recipe_id) && (
                  <option value={d.default_recipe_id}>Previously selected recipe (unavailable)</option>
                )}
                {recipes.map((recipe) => (
                  <option key={recipe.id} value={recipe.id}>{recipe.name}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => saveDefault(d.id)}
              disabled={savingId === d.id || (selectedDefaults[d.id] || "") === (d.default_recipe_id || "")}
              className="rounded bg-blue-600 text-white px-3 py-2 text-sm disabled:opacity-50"
            >
              {savingId === d.id ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => handleDelete(d.id)}
              disabled={d.is_default_department}
              title={d.is_default_department ? "The Default triage department cannot be deleted" : undefined}
              className="text-red-600 hover:underline"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
      <DepartmentTypeSettings refreshKey={departments.map(d => d.id).join(",")} />
      <div className="mt-10">
        <h2 className="text-xl font-bold mb-2">Inbound Mailboxes</h2>

        <p className="text-sm text-[var(--nj-text)] mb-4">
          Email addresses that can receive incoming helpdesk tickets.
        </p>

        <form
          onSubmit={handleCreateMailbox}
          className="flex flex-wrap gap-2 mb-4"
        >
          <input
            type="email"
            required
            value={newMailboxEmail}
            onChange={(e) => setNewMailboxEmail(e.target.value)}
            placeholder="helpdesk@example.org"
            className="border rounded p-2 flex-1 min-w-64"
          />

          <select
            value={newMailboxDepartment}
            onChange={(e) => setNewMailboxDepartment(e.target.value)}
            className="rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] text-[var(--nj-text)] p-2"
          >
            <option value="">No department</option>

            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>

          <button
            type="submit"
            disabled={mailboxSaving}
            className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {mailboxSaving ? "Adding..." : "Add mailbox"}
          </button>
        </form>

        <div className="border rounded divide-y">
          {mailboxes.length === 0 && (
            <div className="p-4 text-sm text-[var(--nj-text)]">
              No inbound mailboxes have been configured.
            </div>
          )}

          {mailboxes.map((mailbox) => (
            <div
              key={mailbox.id}
              className="flex flex-wrap gap-4 items-center p-3"
            >
              <div className="flex-1 min-w-64">
                <div className="font-medium text-[var(--nj-text)]">
                  {mailbox.email_address}
                </div>
              </div>

              <select
                value={mailbox.department_id || ""}
                onChange={(e) =>
                  handleMailboxDepartmentChange(
                    mailbox.id,
                    e.target.value
                  )
                }
                className="rounded border border-[var(--nj-border)] bg-[var(--nj-surface-secondary)] text-[var(--nj-text)] p-2 min-w-52"
              >
                <option value="">No department</option>

                {departments.map((department) => (
                  <option
                    key={department.id}
                    value={department.id}
                  >
                    {department.name}
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-2 text-sm text-[var(--nj-text)]">
                <input
                  type="checkbox"
                  checked={mailbox.is_active}
                  onChange={(e) =>
                    handleMailboxActiveChange(
                      mailbox.id,
                      e.target.checked
                    )
                  }
                />
                Active
              </label>

              <button
                type="button"
                onClick={() => handleDeleteMailbox(mailbox.id)}
                className="text-red-600 hover:underline"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}